import { getGenericError } from './errors';

import {
  EARNINGS_SUMMARY_PATH,
  EARNINGS_TRANSACTIONS_PATH,
  EVENT_STREAM_PATH,
  EVENT_STREAM_EVENT_TYPES,
  EVENT_STREAM_APP_TYPES,
  SUSTAINABILITY_IMPACT_USER_PATH,
  OFFERS_PATH,
  SLEEK_OFFERS_PATH,
  SLEEK_OFFER_AFFILIATE_URL_PATH,
  SLEEK_OFFER_AFFILIATE_URL_PATH_PARAM,
  PUBLIC_OFFERS_PATH,
  REWARDS_PATH,
  REWARD_ID_PARAM,
  REWARDS_CLAIM_PATH,
  REWARDS_USED_PATH,
  STORE_ID_PARAM,
  STORE_PATH,
  REWARDS_USER_PATH,
  REWARDS_OFFER_PATH,
  CONFIGURATION_PATH,
  REWARD_PATH,
  REWARDS_CODE_BY_UID_AND_BY_USER,
  REWARDS_STORE_PATH,
  CLOSET_CASH_DISCOUNT_CODES_PATH,
  SHOPIFY_STORE_PARAM,
  SHOPIFY_STORES,
  CLOSET_CASH_DISCOUNT_CODE_CLAIM_PATH,
  DISCOUNT_CODE_ID_PARAM,
  REGENERATION_SHIPPING_LABEL_PATH,
  REDEMPTION_CODE_PARAM,
  CATEGORIES_PATH,
  EARNINGS_CAROUSEL_PATH,
  USERS_ADDRESSES_PATH,
  USERS_STATUS_PATH,
  SEND_SIGN_IN_EMAIL_LINK_PATH,
  SHIPPING_PROVIDERS_PATH,
  SUBSCRIBE_PATH,
  REDEMPTIONS_PATH,
  GIFT_CARDS_BRANDS_PATH,
  GIFT_CARDS_CATEGORIES_PATH,
  GIFT_CARDS_BRAND_PATH,
  GIFT_CARDS_BRAND_SEARCH_PATH,
  BRAND_ID_PARAM,
  GIFT_CARDS_ISSUE_PATH,
  GIFT_CARD_ID_PARAM,
  GIFT_CARD_BY_UID_PATH,
  GIFT_CARDS_USER_PATH,
  GIFT_CARDS_USED_PATH,
  AUTH_USERS_PATH,
  SIMILAR_AND_TRENDING_REWARDS_PATH,
  FEATURED_REWARDS_PATH,
  SYSTEM_MESSAGES_PATH,
  PAYMENTS_CREATE_PAY_ON_REG_CHECKOUT_SESSION_PATH,
  PAYMENTS_STATUS_PATH,
  PAYMENTS_CREATE_SETUP_INTENT_PATH,
  PERKS_USER_PATH,
  REWARDS_EXCLUSIVE_PATH,
  GIFT_CARDS_BRANDS_OFFER_PATH,
  REWARDS_USER_EXCLUSIVE_DEALS_PATH,
  EVENT_TICKETS_COUNT_PATH,
  EVENT_TICKETS_CLAIM_PATH,
  EVENT_ID_PARAM,
  BROWN_UNIVERSITY_EVENT_ID,
  MARKETING_ITERABLE_SUBSCRIPTION_PATH,
  REWARDS_PAID_PLACEMENTS_PATH,
  USERS_MEMBERSHIP_ADDRESSES_PATH,
  USERS_MILESTONE_PROGRESS_PATH,
  USERS_CREATE_ADDRESS_PATH,
  ADDRESS_UID_PARAM,
  USERS_UPDATE_ADDRESS_PATH,
  SUBSCRIPTIONS_CLAIM_TBB_PATH,
  SUBSCRIPTIONS_CREATE_CHECKOUT_SESSION_PATH,
  SUBSCRIPTIONS_CREATE_ELEMENTS_INTENT_PATH,
  SUBSCRIPTIONS_FINALIZE_ELEMENTS_PAYMENT_PATH,
  SUBSCRIPTIONS_ESTIMATE_TAX_PATH,
  SUBSCRIPTIONS_ORDERS_PATH,
  SUBSCRIPTIONS_PATH,
  SUBSCRIPTION_ID_PARAM,
  SUBSCRIPTIONS_CANCEL_PATH,
  SUBSCRIPTIONS_UPDATE_PATH,
  SLEEK_PUBLIC_OFFERS_PATH,
  CHECKOUT_SESSION_ID_PARAM,
  SUBSCRIPTIONS_CHECKOUT_SESSION_PATH,
  SUBSCRIPTIONS_PAYMENT_INTENT_STATUS_PATH,
  PAYMENT_INTENT_ID_PARAM,
  MEMBERSHIP_PRODUCTS_PRICES_PATH,
  SAVED_MONEY_PATH,
  PAYMENTS_CREATE_PAYMENT_INTENT_PATH,
  PAYMENTS_ORDER_CHECKOUT_SESSION_PATH,
  MEMBERSHIP_VALIDATE_SHIPPING_PATH,
  SUBSCRIPTIONS_PLANS_PATH,
  SUBSCRIPTIONS_EMAIL_ELIGIBILITY_PATH,
  PAYMENTS_ORDER_CREATE_CHECKOUT_SESSION_PATH,
  FEDEX_LOCATIONS_PATH,
  REWARDS_STORE_PATH_PUBLIC,
  REWARDS_STORES_PATH,
  AUTH_PHONE_VERIFY_PATH,
  AUTH_PHONE_PATH,
  AUTH_PHONE_LOGIN_PATH,
  AUTH_MFA_ENABLE_PATH,
  SEND_EMAIL_VERIFICATION_PATH,
  SEND_PASSWORD_RESET_EMAIL_PATH,
  FORCE_RESET_PASSWORD_PATH,
} from '../config/service';

import crud, { apiFetch } from '../../../api/crud';
import { REWARD_PREVIEW_TYPES } from '../config/rewards';
import { US_COUNTRY_CODE } from './locations';
import { isProd } from '../../../config/config';
import { TRASHIE_GA_TAG_ID } from '../config/config';
import { getStoredUtms, getReferrerOrigin } from './url';

const get = async ({ path, options, params }) => {
  const queryString = new URLSearchParams({
    ...(options ? { options: JSON.stringify(options) } : {}),
    ...params,
  }).toString();

  const pathString = queryString ? `${path}?${queryString}` : path;

  const data = await apiFetch({ path: pathString });

  return data;
};

const post = async ({ path, body, authToken }) => {
  const data = await apiFetch({ path, body: JSON.stringify(body), method: 'POST', authToken });

  return data;
};

const serializeSearchParams = (searchParams) => {
  if (!searchParams) return undefined;

  try {
    return Object.fromEntries(searchParams);
  } catch {
    return undefined;
  }
};

// TODO: Uncomment this once we have a PUT endpoint
// const put = async ({ path, body }) => {
//   const data = await apiFetch({ path, body: JSON.stringify(body), method: 'PUT' });

//   return data;
// };

const patch = async ({ path, body }) => {
  const data = await apiFetch({ path, body: JSON.stringify(body), method: 'PATCH' });

  return data;
};

const checkResponseError = ({ status, error }) => {
  let errorMessage = { message: getGenericError() };

  if (status > 202) {
    if (error) {
      const { code, message } = error;

      errorMessage = { code, message: message || getGenericError() };
    }
  }

  return errorMessage;
};

const wait = (delay) => new Promise((resolve) => { setTimeout(resolve, delay); });

const withRetry = async (
  requestFn,
  tries = 3,
  waitTime = 500,
  onCheckError = () => false,
) => {
  try {
    const response = await requestFn();

    if (tries > 1 && onCheckError(response)) {
      return wait(waitTime).then(() => withRetry(requestFn, tries - 1, waitTime, onCheckError));
    }

    return response;
  } catch (err) {
    if (tries <= 1) {
      throw err;
    }

    return wait(waitTime).then(() => withRetry(requestFn, tries - 1, waitTime, onCheckError));
  }
};

const getWithRetry = (
  { path, options, params },
  tries = 3,
  waitTime = 500,
  onCheckError = () => false,
) => withRetry(() => get({ path, options, params }), tries, waitTime, onCheckError);

const postWithRetry = (
  { path, body },
  tries = 3,
  waitTime = 500,
  onCheckError = () => false,
) => withRetry(() => post({ path, body }), tries, waitTime, onCheckError);

const getUser = async (uid) => {
  let data = [];
  let error = '';

  try {
    data = await crud.get({
      path: '/users',
      options: {
        where: {
          uid,
        },
      },
    });
  } catch ({ message }) {
    error = message;
  }

  return { data: data[0] ?? null, error };
};

const getSummary = async () => {
  let data = {};
  let error = '';

  try {
    const response = await get({
      path: EARNINGS_SUMMARY_PATH,
    });

    data = response.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data, error };
};

const getTransactions = async () => {
  let data = [];
  let error = '';

  try {
    const response = await get({
      path: EARNINGS_TRANSACTIONS_PATH,
    });

    data = response.data ?? [];
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data, error };
};

const sendEventStream = async ({
  event = EVENT_STREAM_EVENT_TYPES.USER_REGISTERED,
  application = EVENT_STREAM_APP_TYPES.TRASHIE,
  data = {},
}) => {
  let responseData = {};
  let error = '';

  try {
    const response = await post({
      path: EVENT_STREAM_PATH,
      body: {
        event,
        application,
        data,
      },
    });

    responseData = response.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const syncUser = async (uid, email) => {
  let responseData = {};
  let error = '';

  try {
    const response = await post({
      path: '/users',
      body: {
        data: {
          email,
          uid,
          meta: {
            migrated: false,
            application: EVENT_STREAM_APP_TYPES.TRASHIE,
          },
        },
      },
    });

    responseData = response?.createData ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const createOrFetchUser = async (email, method, shouldSubscribe = true, additionalData = {}) => {
  let responseData = {};
  let error = '';
  const originUrl = getReferrerOrigin();

  try {
    const response = await post({
      path: AUTH_USERS_PATH,
      body:
        {
          firstName: '',
          lastName: '',
          email,
          data: {
            shouldSubscribe,
            method,
            originUrl,
            ...additionalData,
          },

        },
    });

    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }
  return { data: responseData, error };
};

const updateUser = async ({ firstName, lastName, forcePwdReset }) => {
  let responseData = {};
  let error = '';

  try {
    const response = await patch({
      path: '/users',
      body: {
        data: {
          firstName,
          lastName,
          forcePwdReset,
        },
      },
    });

    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const getSustainabilityImpactUser = async () => {
  let responseData = [];
  let error = '';

  try {
    const response = await get({
      path: SUSTAINABILITY_IMPACT_USER_PATH,
    });

    responseData = response?.data ?? [];
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const getOffers = async () => {
  let responseData = [];
  let error = '';

  try {
    const response = await get({
      path: OFFERS_PATH,
    });

    responseData = response?.data ?? [];
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const getSleekOfferAffiliateUrl = async (offerId) => {
  let responseData = [];
  let error = '';

  try {
    const response = await get({
      path: SLEEK_OFFER_AFFILIATE_URL_PATH.replace(SLEEK_OFFER_AFFILIATE_URL_PATH_PARAM, offerId),
    });

    responseData = response?.data ?? [];
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const getSleekOffers = async () => {
  let responseData = [];
  let error = '';

  try {
    const response = await get({
      path: SLEEK_OFFERS_PATH,
    });

    responseData = response?.data ?? [];
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const getSleekPublicOffers = async () => {
  let responseData = [];
  let error = '';

  try {
    const response = await get({
      path: SLEEK_PUBLIC_OFFERS_PATH,
    });

    responseData = response?.data ?? [];
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const getPublicOffers = async () => {
  let responseData = [];
  let error = '';

  try {
    const response = await get({
      path: PUBLIC_OFFERS_PATH,
    });

    responseData = response?.data ?? [];
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const getSimilarAndTrendingRewards = async ({ pageSize, page, isHot, rewardId, ...rest }) => {
  let responseData = [];
  let error = '';
  const queryParams = isHot ? { isHot: true } : {};

  try {
    const response = await get({
      path: SIMILAR_AND_TRENDING_REWARDS_PATH,
      params: {
        pageSize,
        page,
        rewardId,
        ...queryParams,
        ...rest,
      },
    });

    responseData = response?.data ?? [];
  } catch (err) {
    error = checkResponseError(err);
  }

  return {
    data: responseData,
    error,
  };
};

const getFeaturedRewards = async (rest) => {
  let responseData = [];
  let error = '';

  try {
    const response = await get({
      path: FEATURED_REWARDS_PATH,
      params: rest,
    });

    responseData = response?.data ?? [];
  } catch (err) {
    error = checkResponseError(err);
  }

  return {
    data: responseData,
    error,
  };
};

const getUserExclusiveDeals = async ({ pageSize, page, ...rest }) => {
  let responseData = [];
  let error = '';

  try {
    const response = await get({
      path: REWARDS_USER_EXCLUSIVE_DEALS_PATH,
      params: {
        pageSize,
        page,
        ...rest,
      },
    });

    responseData = response?.data ?? [];
  } catch (err) {
    error = checkResponseError(err);
  }

  return {
    data: responseData,
    error,
  };
};

const getRewards = async ({
  pageSize,
  page,
  type = REWARD_PREVIEW_TYPES.DEFAULT,
  ...rest
}) => {
  let responseData = [];
  let error = '';

  if ([REWARD_PREVIEW_TYPES.SIMILAR, REWARD_PREVIEW_TYPES.TRENDING].includes(type)) {
    return getSimilarAndTrendingRewards({ pageSize, page, ...rest });
  }

  if (type === REWARD_PREVIEW_TYPES.FEATURED) {
    return getFeaturedRewards({ ...rest });
  }

  if (type === REWARD_PREVIEW_TYPES.EXCLUSIVE) {
    return getUserExclusiveDeals({ pageSize, page, ...rest });
  }

  try {
    const response = await get({
      path: REWARDS_PATH,
      params: {
        pageSize,
        page,
        ...rest,
      },
    });

    responseData = response?.data ?? [];
  } catch (err) {
    error = checkResponseError(err);
  }

  return {
    data: responseData,
    error,
  };
};

const getRewardsOffer = async ({ pageSize, page, ...rest }) => {
  let responseData = [];
  let error = '';

  try {
    const response = await get({
      path: REWARDS_OFFER_PATH,
      params: {
        pageSize,
        page,
        ...rest,
      },
    });

    responseData = response?.data ?? [];
  } catch (err) {
    error = checkResponseError(err);
  }

  return {
    data: responseData,
    error,
  };
};

const getRewardsPaidPlacements = async ({ pageSize, page, ...rest }) => {
  let responseData = [];
  let error = '';

  try {
    const response = await get({
      path: REWARDS_PAID_PLACEMENTS_PATH,
      params: {
        pageSize,
        page,
        ...rest,
      },
    });

    responseData = response?.data ?? [];
  } catch (err) {
    error = checkResponseError(err);
  }

  return {
    data: responseData,
    error,
  };
};

const getReward = async (rewardId) => {
  let responseData = {};
  let error = '';

  try {
    const response = await get({
      path: REWARD_PATH.replace(REWARD_ID_PARAM, rewardId),
    });

    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return {
    data: responseData,
    error,
  };
};

const patchRewardCodeIsUsed = async ({
  rewardId,
  isUsed,
}) => {
  let responseData = {};
  let error = '';

  try {
    const response = await patch({
      path: REWARDS_USED_PATH.replace(REWARD_ID_PARAM, rewardId),
      body: { isUsed },
    });

    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return {
    data: responseData,
    error,
  };
};

const patchGiftCardsIsUsed = async ({
  giftCardsId,
  isUsed,
}) => {
  let responseData = {};
  let error = '';

  try {
    const response = await patch({
      path: GIFT_CARDS_USED_PATH.replace(GIFT_CARD_ID_PARAM, giftCardsId),
      body: { isUsed },
    });

    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return {
    data: responseData,
    error,
  };
};

const getUserPerks = async ({
  pageSize,
  page,
  orderBy,
  orderType,
  status,
}) => {
  let responseData = {};
  let error = '';

  try {
    const response = await get({
      path: PERKS_USER_PATH,
      params: {
        pageSize,
        page,
        orderBy,
        orderType,
        status,
      },
    });

    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return {
    data: responseData,
    error,
  };
};

const getUserRewards = async ({
  pageSize,
  page,
  orderBy,
  orderType,
  type,
  status,
}) => {
  let responseData = {};
  let error = '';

  try {
    const response = await get({
      path: REWARDS_USER_PATH,
      params: {
        pageSize,
        page,
        orderBy,
        orderType,
        type,
        status,
      },
    });

    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return {
    data: responseData,
    error,
  };
};

const getUserAddresses = async ({
  pageSize,
}) => {
  let responseData = {};
  let error = '';

  try {
    const response = await get({
      path: USERS_ADDRESSES_PATH,
      params: {
        pageSize,
      },
    });

    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return {
    data: responseData,
    error,
  };
};

const getMilestoneProgress = async () => {
  let responseData = {};
  let error = '';

  try {
    const response = await get({
      path: USERS_MILESTONE_PROGRESS_PATH,
    });

    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return {
    data: responseData,
    error,
  };
};

const getUserStatus = async (email, phone, countryCode) => {
  let responseData = {};
  let error = '';

  try {
    const params = {};
    if (email) params.email = email;
    if (phone) params.phone = phone;
    if (countryCode) params.countryCode = countryCode;

    const response = await get({
      path: USERS_STATUS_PATH,
      params,
    });

    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return {
    data: responseData,
    error,
  };
};

const getRewardCodeByUidAndUser = async (rewardCodeUid) => {
  let responseData = {};
  let error = '';

  try {
    const response = await get({
      path: REWARDS_CODE_BY_UID_AND_BY_USER.replace(REWARD_ID_PARAM, rewardCodeUid),
    });

    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return {
    data: responseData,
    error,
  };
};

const getStoreRewards = async (storeId, isPublic = false) => {
  let responseData = [];
  let error = '';

  try {
    const response = await get({
      path: (isPublic ? REWARDS_STORE_PATH_PUBLIC : REWARDS_STORE_PATH)
        .replace(STORE_ID_PARAM, storeId),
    });

    responseData = response?.data ?? [];
  } catch (err) {
    error = checkResponseError(err);
  }

  return {
    data: responseData,
    error,
  };
};

const getStores = async ({ pageSize, page, categoryId, sort } = {}) => {
  let responseData = { stores: [], total: 0 };
  let error = '';

  try {
    const response = await get({
      path: REWARDS_STORES_PATH,
      params: {
        ...(pageSize && { pageSize }),
        ...(page != null && { page }),
        ...(categoryId && { categoryId }),
        ...(sort && { sort }),
      },
    });

    responseData = response?.data ?? { stores: [], total: 0 };
  } catch (err) {
    error = checkResponseError(err);
  }

  return {
    data: responseData,
    error,
  };
};

const patchReward = async (rewardId, wfReturnUrl) => {
  let responseData = {};
  let error = null;

  try {
    const response = await patch({
      path: REWARDS_CLAIM_PATH.replace(REWARD_ID_PARAM, rewardId),
      ...(wfReturnUrl && { body: { wfReturnUrl } }),
    });

    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return {
    data: responseData,
    error,
  };
};

const getStore = async (storeId) => {
  let data = [];
  let error = '';

  try {
    data = await crud.get({
      path: STORE_PATH.replace(STORE_ID_PARAM, storeId),
    });
  } catch ({ message }) {
    error = message;
  }

  return { data, error };
};

const getConfigurations = async (property) => {
  let error = '';
  let valueResponse;
  try {
    const { value } = await crud.get({ path: `${CONFIGURATION_PATH}/${property}` });
    valueResponse = value;
  } catch (err) {
    error = err;
  }

  return { value: valueResponse, error };
};

const getCCDiscountCodes = async (
  shop = SHOPIFY_STORES.CLOSET_CASH_STORE,
  maxAmount = 0,
) => {
  let responseData = [];
  let error = '';

  try {
    const response = await get({
      path: CLOSET_CASH_DISCOUNT_CODES_PATH.replace(SHOPIFY_STORE_PARAM, shop),
      params: { maxAmount },
    });

    responseData = response?.data ?? [];
  } catch ({ message }) {
    error = message;
  }

  return {
    data: responseData,
    error,
  };
};

const postCCDiscountCode = async (
  discountCodeId,
  shop = SHOPIFY_STORES.CLOSET_CASH_STORE,
) => {
  let responseData = {};
  let error = null;

  try {
    const response = await post({
      path: CLOSET_CASH_DISCOUNT_CODE_CLAIM_PATH
        .replace(SHOPIFY_STORE_PARAM, shop)
        .replace(DISCOUNT_CODE_ID_PARAM, discountCodeId),
    });

    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return {
    data: responseData,
    error,
  };
};

const postShippingLabelRegenerate = async ({ redemptionCode, shippingMethodId = null }) => {
  let responseData = {};
  let error = '';

  try {
    const response = await post({
      path: REGENERATION_SHIPPING_LABEL_PATH.replace(REDEMPTION_CODE_PARAM, redemptionCode),
      body: { shippingMethodId },
    });
    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const getCategories = async ({ ...filters } = {}) => {
  let responseData = {};
  let error = '';
  try {
    const response = await get({
      path: CATEGORIES_PATH,
      params: {
        ...filters,
      },
    });

    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return {
    data: responseData,
    error,
  };
};

const getEarningsCarousel = async () => {
  let responseData = [];
  let error = '';

  try {
    const response = await get({
      path: EARNINGS_CAROUSEL_PATH,
    });

    responseData = response?.data ?? [];
  } catch (err) {
    error = checkResponseError(err);
  }

  return {
    data: responseData,
    error,
  };
};

const postSubscribeStatus = async (email, shouldSubscribe = false) => {
  let responseData = {};
  let error = '';

  try {
    const response = await post({
      path: SUBSCRIBE_PATH,
      body: {
        email,
        shouldSubscribe,
      },
    });

    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const fetchShippingProviders = async ({
  storeUid,
  storeId,
  recyclingProductType,
  provinceCode,
}) => {
  let responseData = [];
  let error = '';

  try {
    const response = await post({
      path: SHIPPING_PROVIDERS_PATH,
      body: {
        storeUid,
        storeId,
        recyclingProductType,
        provinceCode,
      },
    });

    responseData = response?.data ?? [];
  } catch (err) {
    error = checkResponseError(err);
  }

  return {
    data: responseData,
    error,
  };
};

const sendSignInEmailLink = async ({
  email,
  returnUrl,
  data,
}) => {
  let responseData = {};
  let error = '';

  try {
    const response = await post({
      path: SEND_SIGN_IN_EMAIL_LINK_PATH,
      body: {
        email,
        returnUrl,
        data,
      },
    });

    responseData = response.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const sendEmailVerification = async ({
  returnUrl,
}) => {
  let responseData = {};
  let error = '';

  try {
    const response = await post({
      path: SEND_EMAIL_VERIFICATION_PATH,
      body: {
        data: {
          returnUrl,
        },
      },
    });

    responseData = response.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const sendPasswordResetEmail = async ({
  email,
  returnUrl,
}) => {
  let responseData = {};
  let error = '';

  try {
    const response = await post({
      path: SEND_PASSWORD_RESET_EMAIL_PATH,
      body: {
        data: {
          email,
          returnUrl,
        },
      },
    });

    responseData = response.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const forceResetPassword = async ({ password }) => {
  let responseData = {};
  let error = '';

  try {
    const response = await post({
      path: FORCE_RESET_PASSWORD_PATH,
      body: {
        data: {
          password,
        },
      },
    });

    responseData = response.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const getRedemption = async (redemptionCode) => {
  let responseData = null;
  let error = '';

  try {
    const response = await getWithRetry(
      { path: `${REDEMPTIONS_PATH}/${redemptionCode}` },
      3,
      1000,
      (resp) => !resp?.data?.labeldata,
    );

    responseData = response?.data ?? null;
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const postRedemptionCode = async ({
  id, pin, email, storeId, storeUid, fromAddress, shippingProviderId,
}) => {
  let responseData = {};
  let error = '';

  try {
    const response = await postWithRetry({
      path: REDEMPTIONS_PATH,
      body: {
        data: {
          id,
          pin,
          email,
          storeId,
          storeUid,
          fromAddress,
          shippingProviderId,
        },
      },
    });

    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const fetchWithRetry = (url, tries = 3) => (
  fetch(url)
    .then(response => {
      if (response.ok) {
        return response.json();
      }

      return Promise.reject(response);
    })
    .catch(error => {
      if (tries < 1) {
        return { error };
      }

      return wait(500).then(() => fetchWithRetry(url, tries - 1));
    })
);

const AI_API_BASE = isProd()
  ? 'https://api.ia.trashie.io/api/v1'
  : 'https://api.ia.dev.trashie.io/api/v1';

const searchDeals = async ({
  q,
  limit,
  itemsPerBrand,
  affiliateCid,
  userSegments,
  isMember,
} = {}) => {
  let responseData = { items: [], total_brands: 0 };
  let error = '';

  const params = new URLSearchParams({ q });
  if (limit != null) params.append('limit', limit);
  if (itemsPerBrand != null) params.append('items_per_brand', itemsPerBrand);
  if (affiliateCid) params.append('affiliate_cid', affiliateCid);
  if (userSegments) params.append('user_segments', userSegments);
  if (isMember != null) params.append('is_member', isMember);

  try {
    const result = await fetchWithRetry(`${AI_API_BASE}/search/?${params}`);
    if (result?.error) {
      error = checkResponseError(result.error);
    } else {
      responseData = result?.result ?? responseData;
    }
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const fetchShippingProviderLocations = async ({
  URL,
  distance,
  zipcode,
  retailer,
  address,
  limit = 3,
}) => {
  let data = [];
  let error = '';

  if (!URL) {
    return {
      data,
      error,
    };
  }

  const searchParams = new URLSearchParams({
    distance,
    zipcode,
    retailer,
    address,
  });

  const result = await fetchWithRetry(`${URL}?${searchParams}`);

  if (result.error) {
    error = checkResponseError(result.error);
  } else {
    data = (result.locations ?? []).splice(0, limit);
  }

  return {
    data,
    error,
  };
};

const getFedexLocations = async ({
  address,
  distance,
}) => {
  let responseData = {};
  let error = '';
  try {
    const body = {
      distance: {
        units: 'MI',
        value: distance,
      },
      location: {
        address: {
          streetLines: [address.address1],
          city: address.city,
          stateOrProvinceCode: address.provinceCode,
          postalCode: address.zip,
          countryCode: US_COUNTRY_CODE,
        },
      },
    };
    const response = await post({
      path: FEDEX_LOCATIONS_PATH,
      body,
    });
    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const getTakeBackBagDetails = async (registerCode) => {
  let responseData = {};
  let error = '';

  try {
    const response = await get({
      path: `/redemptions/${registerCode}`,
    });
    if (response.error) {
      error = response.error;
    }
    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }
  return { data: responseData, error };
};

const getGiftCardsCategories = async ({ ...filters }) => {
  let responseData = {};
  let error = '';
  try {
    const response = await get({
      path: GIFT_CARDS_CATEGORIES_PATH,
      params: {
        ...filters,
      },
    });

    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return {
    data: responseData,
    error,
  };
};

// GIFT_CARDS_BRAND_SEARCH_PATH
const getGiftCardsBrandsByName = async (name) => {
  let responseData = [];
  let error = '';

  try {
    const response = await get({
      path: GIFT_CARDS_BRAND_SEARCH_PATH,
      params: {
        name,
      },
    });

    responseData = response?.data?.brands ?? [];
  } catch (err) {
    error = checkResponseError(err);
  }
  return {
    data: responseData,
    error,
  };
};

const getGiftCardsBrands = async ({ pageSize, page, ...rest }) => {
  let responseData = [];
  let error = '';

  try {
    const response = await get({
      path: GIFT_CARDS_BRANDS_PATH,
      params: {
        pageSize,
        page,
        ...rest,
      },
    });

    responseData = response?.data ?? [];
  } catch (err) {
    error = checkResponseError(err);
  }

  return {
    data: responseData,
    error,
  };
};

const getGiftCardsBrandsOffer = async ({ pageSize, page, ...rest }) => {
  let responseData = [];
  let error = '';

  try {
    const response = await get({
      path: GIFT_CARDS_BRANDS_OFFER_PATH,
      params: {
        pageSize,
        page,
        ...rest,
      },
    });

    responseData = response?.data ?? [];
  } catch (err) {
    error = checkResponseError(err);
  }

  return {
    data: responseData,
    error,
  };
};

const getGiftCardBrand = async (brandId) => {
  let responseData = {};
  let error = '';

  try {
    const response = await get({
      path: GIFT_CARDS_BRAND_PATH.replace(BRAND_ID_PARAM, brandId),
    });

    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return {
    data: responseData,
    error,
  };
};

const issueGiftCard = async ({
  brand,
  amount,
}) => {
  let responseData = {};
  let error = null;

  try {
    const response = await post({
      path: GIFT_CARDS_ISSUE_PATH,
      body: { brand, amount },
    });

    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return {
    data: responseData,
    error,
  };
};

const getGiftCardByUid = async (giftCardId) => {
  let responseData = {};
  let error = '';

  try {
    const response = await get({
      path: GIFT_CARD_BY_UID_PATH.replace(GIFT_CARD_ID_PARAM, giftCardId),
    });

    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return {
    data: responseData,
    error,
  };
};

const getUserGiftCards = async ({
  pageSize,
  page,
  orderBy,
  orderType,
  status,
}) => {
  let responseData = {};
  let error = '';

  try {
    const response = await get({
      path: GIFT_CARDS_USER_PATH,
      params: {
        pageSize,
        page,
        orderBy,
        orderType,
        status,
      },
    });

    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return {
    data: responseData,
    error,
  };
};

const getSystemMessages = async () => {
  let responseData = {};
  let error = '';

  try {
    const response = await get({
      path: SYSTEM_MESSAGES_PATH,
    });

    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return {
    data: responseData,
    error,
  };
};

const TRASHIE_GA_MEASUREMENT_ID = isProd()
  ? TRASHIE_GA_TAG_ID.PRODUCTION
  : TRASHIE_GA_TAG_ID.STAGING;

const getSearchParamValue = (searchParams, key) => {
  if (!searchParams) return null;
  if (typeof searchParams.get === 'function') {
    return searchParams.get(key);
  }
  return searchParams[key] || null;
};

const getGaClientId = async () => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return null;
  }

  return new Promise((resolve) => {
    const timeout = setTimeout(() => resolve(null), 500);
    window.gtag('get', TRASHIE_GA_MEASUREMENT_ID, 'client_id', (clientId) => {
      clearTimeout(timeout);
      resolve(clientId ? String(clientId) : null);
    });
  });
};

const getGaSessionId = async () => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return null;
  }

  return new Promise((resolve) => {
    const timeout = setTimeout(() => resolve(null), 500);
    window.gtag('get', TRASHIE_GA_MEASUREMENT_ID, 'session_id', (sessionId) => {
      clearTimeout(timeout);
      resolve(sessionId ? String(sessionId) : null);
    });
  });
};

const getCookieValue = (name) => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
  if (!match?.[1]) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

const buildCheckoutAttributionPayload = async (searchParams) => {
  const storedUtms = getStoredUtms() || {};
  const getAttribution = (key) => getSearchParamValue(searchParams, key) || storedUtms[key] || null;

  const payload = {
    gaClientId: await getGaClientId(),
    gaSessionId: await getGaSessionId(),
    utmSource: getAttribution('utm_source'),
    utmMedium: getAttribution('utm_medium'),
    utmCampaign: getAttribution('utm_campaign'),
    utmTerm: getAttribution('utm_term'),
    utmContent: getAttribution('utm_content'),
    gclid: getAttribution('gclid'),
    fbclid: getAttribution('fbclid'),
    msclkid: getAttribution('msclkid'),
    fbc: getCookieValue('_fbc'),
    fbp: getCookieValue('_fbp'),
  };

  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== null && value !== ''),
  );
};

const createPayOnRegCheckoutSession = async (
  redemptionCode,
  couponId = null,
  productType = null,
  searchParams = null,
) => {
  let responseData = {};
  let error = '';
  const serializedSearchParams = serializeSearchParams(searchParams);

  try {
    const attributionData = await buildCheckoutAttributionPayload();
    const response = await post({
      path: PAYMENTS_CREATE_PAY_ON_REG_CHECKOUT_SESSION_PATH
        .replace(REDEMPTION_CODE_PARAM, redemptionCode),
      body: {
        couponId,
        ...(productType && { productType }),
        ...(serializedSearchParams && { searchParams: serializedSearchParams }),
        ...attributionData,
      },
    });
    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const getCheckoutSession = async (checkoutSessionId) => {
  let responseData = {};
  let error = '';

  try {
    const response = await get({
      path: SUBSCRIPTIONS_CHECKOUT_SESSION_PATH.replace(
        CHECKOUT_SESSION_ID_PARAM,
        checkoutSessionId,
      ),
    });
    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const getOrderCheckoutSession = async (checkoutSessionId) => {
  let responseData = {};
  let error = '';

  try {
    const response = await get({
      path: PAYMENTS_ORDER_CHECKOUT_SESSION_PATH.replace(
        CHECKOUT_SESSION_ID_PARAM,
        checkoutSessionId,
      ),
    });
    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const getSubscriptionPaymentIntentStatus = async (paymentIntentId) => {
  let responseData = {};
  let error = '';

  try {
    const response = await get({
      path: SUBSCRIPTIONS_PAYMENT_INTENT_STATUS_PATH.replace(
        PAYMENT_INTENT_ID_PARAM,
        paymentIntentId,
      ),
    });
    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const getSubscriptionEmailEligibility = async (email) => {
  let responseData = {};
  let error = '';

  try {
    const response = await get({
      path: SUBSCRIPTIONS_EMAIL_ELIGIBILITY_PATH,
      params: { email: email.trim() },
    });
    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const createPaymentIntent = async (redemptionCode) => {
  let responseData = {};
  let error = '';

  try {
    const response = await post({
      path: PAYMENTS_CREATE_PAYMENT_INTENT_PATH.replace(REDEMPTION_CODE_PARAM, redemptionCode),
    });
    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const getPaymentStatus = async (redemptionCode, retry = false) => {
  let responseData = {};
  let error = '';

  const path = PAYMENTS_STATUS_PATH.replace(REDEMPTION_CODE_PARAM, redemptionCode);

  try {
    const response = retry
      ? await getWithRetry(
        { path },
        3,
        2000,
        (resp) => resp.data?.isPayOnShip === true && resp.data?.isPaid === false,
      )
      : await get({ path });

    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const createSubscriptionCheckoutSession = async ({
  products,
  address,
  couponId,
  isDirectPayment,
  searchParams,
}) => {
  let responseData = {};
  let error = '';
  const serializedSearchParams = serializeSearchParams(searchParams);

  try {
    const attributionData = await buildCheckoutAttributionPayload(searchParams);
    const response = await post({
      path: SUBSCRIPTIONS_CREATE_CHECKOUT_SESSION_PATH,
      body: {
        products,
        shippingAddress: address,
        couponId,
        isDirectPayment,
        ...(serializedSearchParams && { searchParams: serializedSearchParams }),
        searchParams: searchParams && Object.fromEntries(searchParams),
        ...attributionData,
      },
    });
    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const createSubscriptionElementsIntent = async ({
  products,
  couponId,
  shippingAddress,
  email,
  phone,
  firstName,
  lastName,
  isDirectPayment,
  searchParams,
}) => {
  let responseData = {};
  let error = '';
  const serializedSearchParams = serializeSearchParams(searchParams);

  try {
    const attributionData = await buildCheckoutAttributionPayload(searchParams);
    const response = await post({
      path: SUBSCRIPTIONS_CREATE_ELEMENTS_INTENT_PATH,
      body: {
        products,
        couponId,
        shippingAddress,
        email,
        phone,
        firstName,
        lastName,
        isDirectPayment,
        ...(serializedSearchParams && { searchParams: serializedSearchParams }),
        ...attributionData,
      },
    });
    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const finalizeSubscriptionElementsPayment = async ({
  paymentIntentId,
  products,
  couponId,
  shippingAddress,
  email,
  phone,
  firstName,
  lastName,
  marketingOptedIn,
  smsMarketingOptedIn,
  isDirectPayment,
  searchParams,
}) => {
  let responseData = {};
  let error = '';
  const serializedSearchParams = serializeSearchParams(searchParams);
  const emailMarketingOptedIn = Boolean(marketingOptedIn);
  const smsMarketingOptedInBool = Boolean(smsMarketingOptedIn);

  try {
    const attributionData = await buildCheckoutAttributionPayload(searchParams);
    const response = await post({
      path: SUBSCRIPTIONS_FINALIZE_ELEMENTS_PAYMENT_PATH,
      body: {
        paymentIntentId,
        products,
        couponId,
        shippingAddress,
        email,
        phone,
        firstName,
        lastName,
        marketingOptedIn: emailMarketingOptedIn,
        smsMarketingOptedIn: smsMarketingOptedInBool,
        isDirectPayment,
        ...(serializedSearchParams && { searchParams: serializedSearchParams }),
        ...attributionData,
      },
    });
    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const estimateSubscriptionTax = async (address) => {
  let responseData = {};
  let error = '';

  try {
    const response = await post({
      path: SUBSCRIPTIONS_ESTIMATE_TAX_PATH,
      body: { address },
    });
    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const updateMembershipSubscription = async (subscriptionId, defaultPaymentMethod) => {
  let responseData = {};
  let error = '';

  try {
    const response = await patch({
      path: SUBSCRIPTIONS_UPDATE_PATH.replace(SUBSCRIPTION_ID_PARAM, subscriptionId),
      body: {
        defaultPaymentMethod,
      },
    });
    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const createSetupIntent = async () => {
  let responseData = {};
  let error = '';

  try {
    const response = await post({
      path: PAYMENTS_CREATE_SETUP_INTENT_PATH,
    });
    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const createOrderCheckoutSession = async ({
  products,
  userAddressId,
  shippingAddress,
  shippingPrice,
  searchParams,
}) => {
  let responseData = {};
  let error = '';
  const serializedSearchParams = serializeSearchParams(searchParams);

  try {
    const attributionData = await buildCheckoutAttributionPayload(searchParams);
    const response = await post({
      path: PAYMENTS_ORDER_CREATE_CHECKOUT_SESSION_PATH,
      body: {
        products,
        userAddressId,
        shippingAddress,
        shippingPrice,
        ...(serializedSearchParams && { searchParams: serializedSearchParams }),
        ...attributionData,
      },
    });
    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const validateShipping = async ({ type, products, shippingAddress }) => {
  let responseData = {};
  let error = '';

  try {
    const response = await post({
      path: MEMBERSHIP_VALIDATE_SHIPPING_PATH,
      body: { type, products, shippingAddress },
    });
    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const getRewardsExclusive = async ({ redemptionCode, pageSize, page, ...rest }) => {
  let responseData = [];
  let error = '';

  try {
    const response = await get({
      path: REWARDS_EXCLUSIVE_PATH.replace(REDEMPTION_CODE_PARAM, redemptionCode),
      params: {
        pageSize,
        page,
        ...rest,
      },
    });

    responseData = response?.data ?? [];
  } catch (err) {
    error = checkResponseError(err);
  }

  return {
    data: responseData,
    error,
  };
};

const getRemainingBrownTickets = async () => {
  let responseData;
  let error = '';

  try {
    const res = await get({
      path: EVENT_TICKETS_COUNT_PATH.replace(EVENT_ID_PARAM, BROWN_UNIVERSITY_EVENT_ID),
    });

    responseData = res.data;
  } catch (err) {
    error = checkResponseError(err);
  }

  return {
    data: responseData,
    error,
  };
};

const signUpUserToBrownCampaign = async ({ holderEmail, holderDetails }) => {
  let responseData;
  let error = '';

  try {
    const res = await post({
      path: EVENT_TICKETS_CLAIM_PATH.replace(EVENT_ID_PARAM, BROWN_UNIVERSITY_EVENT_ID),
      body: { holderEmail, holderDetails },
    });

    responseData = res.data;
  } catch (err) {
    error = checkResponseError(err);
  }

  return {
    data: responseData,
    error,
  };
};

const subscribeUserToIterableList = async ({ listId, email, firstName, lastName, address }) => {
  let responseData;
  let error = '';

  try {
    const res = await post({
      path: MARKETING_ITERABLE_SUBSCRIPTION_PATH,
      body: { listId, email, firstName, lastName, ...address },
    });

    responseData = res.data;
  } catch (err) {
    error = checkResponseError(err);
  }

  return {
    data: responseData,
    error,
  };
};

const getSubscriptionsOrders = async () => {
  let data = {};
  let error = '';

  try {
    const response = await get({
      path: SUBSCRIPTIONS_ORDERS_PATH,
    });

    data = response.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data, error };
};

const getMembershipSubscription = async () => {
  let responseData = {};
  let error = '';

  try {
    const response = await get({ path: SUBSCRIPTIONS_PATH });
    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return {
    data: responseData,
    error,
  };
};

const getMembershipAddresses = async ({ pageSize = 2 }) => {
  let responseData = {};
  let error = '';

  try {
    const response = await get({
      path: `${USERS_MEMBERSHIP_ADDRESSES_PATH}?pageSize=${pageSize}`,
    });

    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const getMembershipProductsPrices = async () => {
  let responseData = {};
  let error = '';

  try {
    const response = await get({
      path: MEMBERSHIP_PRODUCTS_PRICES_PATH,
    });
    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const createUserAddress = async ({ fromAddress }) => {
  let responseData = {};
  let error = '';

  try {
    const response = await post({
      path: USERS_CREATE_ADDRESS_PATH,
      body: {
        data: {
          fromAddress,
        },
      },
    });

    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const updateUserAddress = async ({ addressUid, fromAddress }) => {
  let responseData = {};
  let error = '';

  try {
    const response = await patch({
      path: USERS_UPDATE_ADDRESS_PATH.replace(ADDRESS_UID_PARAM, addressUid),
      body: {
        data: {
          fromAddress,
        },
      },
    });

    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const placeSubscriptionOrder = async ({ products, userAddressId, address }) => {
  let responseData = {};
  let error = '';

  try {
    const response = await post({
      path: SUBSCRIPTIONS_CLAIM_TBB_PATH,
      body: {
        products,
        shippingAddress: address,
        userAddressId,
      },
    });

    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const cancelMembershipRenewalSubscription = async ({ subscriptionId, cancelAtPeriodEnd, cancellationComment = '' }) => {
  let responseData = {};
  let error = '';

  try {
    const response = await post({
      path: SUBSCRIPTIONS_CANCEL_PATH.replace(SUBSCRIPTION_ID_PARAM, subscriptionId),
      body: { cancelAtPeriodEnd, cancellationComment },
    });
    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const getSubscriptionSavedMoney = async () => {
  let responseData = {};
  let error = '';

  try {
    const response = await get({ path: SAVED_MONEY_PATH });
    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const getSubscriptionsPlans = async () => {
  let responseData = {};
  let error = '';

  try {
    const response = await get({ path: SUBSCRIPTIONS_PLANS_PATH });

    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return {
    data: responseData,
    error,
  };
};

const postRegisterPhone = async ({ phoneNumber, countryCode, authToken }) => {
  let responseData = {};
  let error = '';

  try {
    const response = await post({
      path: AUTH_PHONE_PATH,
      body: { phoneNumber, countryCode },
      authToken,
    });

    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const postPhoneVerify = async ({ idToken, authToken }) => {
  let responseData = {};
  let error = '';

  try {
    const response = await post({
      path: AUTH_PHONE_VERIFY_PATH,
      body: { idToken },
      authToken,
    });

    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const postMfaEnable = async ({ authToken } = {}) => {
  let responseData = {};
  let error = '';

  try {
    const response = await post({
      path: AUTH_MFA_ENABLE_PATH,
      authToken,
    });

    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

const postPhoneLogin = async ({ idToken }) => {
  let responseData = {};
  let error = '';

  try {
    const response = await post({
      path: AUTH_PHONE_LOGIN_PATH,
      body: { idToken },
    });

    responseData = response?.data ?? {};
  } catch (err) {
    error = checkResponseError(err);
  }

  return { data: responseData, error };
};

export {
  getUser,
  getSummary,
  getTransactions,
  sendEventStream,
  syncUser,
  createOrFetchUser,
  getSustainabilityImpactUser,
  getOffers,
  getSleekOffers,
  getSleekPublicOffers,
  getSleekOfferAffiliateUrl,
  getRewards,
  getUserRewards,
  getUserAddresses,
  getMilestoneProgress,
  getUserStatus,
  getReward,
  getStoreRewards,
  getStores,
  patchReward,
  patchRewardCodeIsUsed,
  getStore,
  getRewardsOffer,
  getRewardsPaidPlacements,
  getPublicOffers,
  getConfigurations,
  getRewardCodeByUidAndUser,
  getCCDiscountCodes,
  postCCDiscountCode,
  postShippingLabelRegenerate,
  getCategories,
  getEarningsCarousel,
  sendSignInEmailLink,
  sendEmailVerification,
  sendPasswordResetEmail,
  forceResetPassword,
  fetchShippingProviders,
  postSubscribeStatus,
  getRedemption,
  postRedemptionCode,
  fetchShippingProviderLocations,
  getTakeBackBagDetails,
  getGiftCardsCategories,
  getGiftCardsBrands,
  getGiftCardsBrandsOffer,
  getGiftCardsBrandsByName,
  getGiftCardBrand,
  issueGiftCard,
  getGiftCardByUid,
  getUserGiftCards,
  patchGiftCardsIsUsed,
  getSimilarAndTrendingRewards,
  getFeaturedRewards,
  getSystemMessages,
  createPayOnRegCheckoutSession,
  getCheckoutSession,
  getOrderCheckoutSession,
  getSubscriptionPaymentIntentStatus,
  getSubscriptionEmailEligibility,
  getPaymentStatus,
  createSubscriptionCheckoutSession,
  createSubscriptionElementsIntent,
  finalizeSubscriptionElementsPayment,
  estimateSubscriptionTax,
  createSetupIntent,
  createOrderCheckoutSession,
  getUserPerks,
  getRewardsExclusive,
  getUserExclusiveDeals,
  getRemainingBrownTickets,
  signUpUserToBrownCampaign,
  subscribeUserToIterableList,
  getMembershipAddresses,
  createUserAddress,
  updateUserAddress,
  placeSubscriptionOrder,
  getSubscriptionsOrders,
  getMembershipSubscription,
  cancelMembershipRenewalSubscription,
  updateMembershipSubscription,
  getMembershipProductsPrices,
  getSubscriptionSavedMoney,
  createPaymentIntent,
  validateShipping,
  getSubscriptionsPlans,
  getFedexLocations,
  postRegisterPhone,
  postPhoneVerify,
  postMfaEnable,
  postPhoneLogin,
  updateUser,
  searchDeals,
};
